// Home.jsx
import React, { useEffect, useState } from "react";
import { motion, useScroll } from "framer-motion";

import Slider from "./Slider";
import ActivityPage from "./Activity/ActivityPage";
import Statistics from "./Statistics/Statistics";
import DoctorPage from "./Doctors/DoctorPage";
import GalleryPage from "./Gallery/GalleryPage";
import Publishment from "./Publishment/Publishment";
import OurStory from "./OurStory/OurStory";
import Aid from "./Aid/Aid";
import Contact from "./ContactPage/Contact";
import Footer from "./ContactPage/Footer";
import CasePage from "./Cases/CasePage";
import ZakatHome from "./Zakat/ZakatHome";

const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

function Home({ settings }) {
  const { scrollYProgress } = useScroll();
  const [pages, setPages] = useState(2); // Start with 1 page
  const [loading, setLoading] = useState(false);

  // Sections grouped by page (use JSX elements as you already did)
  const sectionsPerPage = [
    [<ActivityPage key="activity" />],
    [
      <Statistics key="statistics" />,
      <OurStory key="ourstory" />,
      <DoctorPage key="doctors" />,
      <CasePage key="cases" />,
      <Publishment key="publishment" />,
      <GalleryPage key="gallery" />,
      <Contact key="contact" />,
      <Footer key="footer" />
    ],
  ];

  const totalPages = sectionsPerPage.length;

  const loadNextPage = () => {
    if (!loading && pages < totalPages) {
      setLoading(true);
      setTimeout(() => {
        setPages(prev => prev + 1);
        setLoading(false);
      }, 1000);
    }
  };

  const handleScroll = () => {
    if (
      window.innerHeight + window.scrollY >= document.body.scrollHeight - 50 &&
      !loading &&
      pages < totalPages
    ) {
      loadNextPage();
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pages, loading]);

  // Helper to inject onVisible into the Statistics element
  const injectOnVisibleToStatistics = (element) => {
    if (!React.isValidElement(element)) return element;
    // check the component type to find the Statistics component
    // This works because you imported Statistics into this file
    if (element.type === Statistics) {
      return React.cloneElement(element, {
        onVisible: () => {
          // set to all pages when statistics enters viewport
          if (pages < totalPages) setPages(totalPages);
        }
      });
    }
    return element;
  };

  return (
    <div className="relative">
      {/* Scroll Progress Indicator */}
      <motion.div
        id="scroll-indicator"
        className="z-50 bg-green-700 rounded-sm"
        style={{
          scaleX: scrollYProgress,
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          height: 10,
          originX: 0,
        }}
      />

      <Slider settings={settings} />

      {/* Render Sections (omit top margin on final page) */}
      {sectionsPerPage.slice(0, pages).map((pageSections, pageIndex) => (
        <React.Fragment key={pageIndex}>
          {pageSections.map((Section, sectionIndex) => (
            <motion.div
              key={sectionIndex}
              initial="hidden"
              whileInView="visible"
              variants={sectionVariants}
              viewport={{ margin: "0px 0px -50px 0px" }}
              className={pageIndex !== totalPages - 1 ? "mt-10 md:mt-24" : ""}
            >
              {injectOnVisibleToStatistics(Section)}
            </motion.div>
          ))}
        </React.Fragment>
      ))}

      {/* Loading Spinner */}
      {loading && pages < totalPages && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      )}
    </div>
  );
}

export default Home;
